import { useEffect, useState } from "react";
import { Form, message } from "antd";
import Upload, { RcFile } from "antd/es/upload";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_VOUCHER, UPDATE_VOUCHER } from "../mutations";
import { VIEW_VOUCHER, LIST_TIERS } from "../queries";
import { DEFAULT_USER_ROLE, ROUTES } from "@/consts";
import { Voucher } from "../queries/types";

type FormData = {
	name: string;
	tier_id: number;
	is_active: boolean;
	file?: any;
	coins?: number;
	image?: any;
};

const shapeFormData = (values: FormData) => {
	const shapedValues = { ...values, roles_ids: [DEFAULT_USER_ROLE] };
	shapedValues.image = values.image?.[0]?.originFileObj;
	shapedValues.file = values.file?.[0]?.originFileObj;
	return shapedValues;
};

const useVoucherForm = () => {
	const { id: voucherId } = useParams();
	const navigate = useNavigate();
	const [voucherForm] = Form.useForm();

	const { data: allTiersData, loading: fetchTiersLoading } =
		useQuery(LIST_TIERS);

	const [createVoucher, { loading: creatingVoucher }] =
		useMutation(CREATE_VOUCHER);

	const [updateVoucher, { loading: updatingVoucher }] =
		useMutation(UPDATE_VOUCHER);

	const { data: voucherData, loading: loadingVoucher } = useQuery(
		VIEW_VOUCHER,
		{
			skip: !voucherId,
			variables: {
				id: voucherId,
			},
		},
	);

	const voucher: Voucher = voucherData?.showVoucher;
	const [imageUrl, setImageUrl] = useState("");
	const [submitStatus, setSubmitStatus] = useState(true);

	const initialValues = {
		name: voucher?.name || "",
		image: (voucher?.image?.url && [{ url: voucher?.image?.url }]) || null,
		file: (voucher?.file?.url && [{ url: voucher?.file?.url }]) || null,
		tier_id: voucher?.tier?.id || null,
		coins: voucher?.coins || null,
	};

	const onCreateEditVoucher = (values: FormData) => {
		const shapedValues = shapeFormData(values);
		if (voucherId) {
			updateVoucher({
				variables: {
					id: voucherId,
					voucher: {
						...shapedValues,
						is_active: submitStatus,
						roles_ids: [DEFAULT_USER_ROLE],
					},
				},
				update: (cache) => {
					cache.evict({ fieldName: "showVoucher" });
					cache.evict({ fieldName: "listVouchers" });
				},
			})
				.then((response) => {
					if (response?.data?.updateVoucher?.statusCode === 200) {
						message.success("Voucher updated successfully");
						navigate(ROUTES.Vouchers, { replace: true });
					}
				})
				.catch((err) => {
					if (err.graphQLErrors[0].statusCode === 422) {
						const { errors } = err.graphQLErrors[0];
						message.error(Object.values(errors)[0] as string);
						return;
					}
					message.error(err?.message || "Something went wrong");
				});
			return;
		}

		createVoucher({
			variables: {
				voucher: {
					...shapedValues,
					is_active: submitStatus,
					roles_ids: [DEFAULT_USER_ROLE],
				},
			},
			update: (cache) => {
				cache.evict({ fieldName: "listVouchers" });
			},
		})
			.then((response) => {
				if (response?.data?.createVoucher?.statusCode === 200) {
					message.success("Voucher created successfully");
					voucherForm.resetFields();
					navigate(ROUTES.Vouchers, { replace: true });
				}
			})
			.catch((err) => {
				if (err.graphQLErrors[0].statusCode === 422) {
					const { errors } = err.graphQLErrors[0];
					message.error(Object.values(errors)[0] as string);
					return;
				}
				message.error(err?.message || "Something went wrong");
			});
	};

	const normFile = (e: any) => {
		if (Array.isArray(e)) {
			return e;
		}
		return e?.fileList;
	};

	const onRemoveImage = () => {
		setImageUrl("");
	};

	const beforeUpload = async (file: RcFile) => {
		if (file.type.split("/")[0] !== "image") {
			message.error("You can only upload image file!");
			return Upload.LIST_IGNORE;
		}
		const isLargerThan1024 = file.size / 1024 / 1024 <= 1;
		if (!isLargerThan1024) {
			message.error("Image must be smaller than 1024kb!");
			return Upload.LIST_IGNORE;
		}
		return false;
	};

	useEffect(() => {
		if (voucher) {
			voucherForm.resetFields();
		}
	}, [voucher, voucherForm]);

	return {
		loading: loadingVoucher || fetchTiersLoading,
		voucherId,
		normFile,
		beforeUpload,
		voucherForm,
		initialValues,
		onCreateEditVoucher,
		creatingVoucher,
		updatingVoucher,
		allTiersData,
		fetchTiersLoading,
		onRemoveImage,
		imageUrl,
		setSubmitStatus,
	};
};

export default useVoucherForm;
